import React, { useState, useEffect, useRef } from "react";
import MovieCard from "./MovieCard";
import SearchIcon from "./search.svg";
import "./app.css";
import { FaVolumeUp, FaVolumeMute, FaExpand } from "react-icons/fa";

const API_URL = "https://www.omdbapi.com?apikey=12b4f0d8";
const YOUTUBE_API_KEY = "AIzaSyDVg6LMYhf0bN2Jzzb7tWFWYItEz67x5MM";
const YOUTUBE_SEARCH_URL = "https://www.googleapis.com/youtube/v3/search";

const App = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [movies, setMovies] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [youtubeVideoId, setYoutubeVideoId] = useState(null);
  const [isLoadingTrailer, setIsLoadingTrailer] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    searchMovies("horror");
  }, []);

  const searchMovies = async (title) => {
    const response = await fetch(`${API_URL}&s=${title}`);
    const data = await response.json();

    if (data.Search) {
      const moviesWithDetails = await Promise.all(
        data.Search.map(async (movie) => {
          const detailsResponse = await fetch(`${API_URL}&i=${movie.imdbID}`);
          const detailsData = await detailsResponse.json();
          return detailsData;
        })
      );

      setMovies(moviesWithDetails);
    } else {
      setMovies([]);
    }
  };

  const searchYoutubeTrailer = async (movieTitle, year) => {
    setIsLoadingTrailer(true);
    try {
      const query = `${movieTitle} ${year} official trailer`;
      const response = await fetch(
        `${YOUTUBE_SEARCH_URL}?part=snippet&maxResults=1&q=${encodeURIComponent(
          query
        )}&key=${YOUTUBE_API_KEY}`
      );
      const data = await response.json();
      if (data.items && data.items.length > 0) {
        setYoutubeVideoId(data.items[0].id.videoId);
      } else {
        setYoutubeVideoId(null);
        alert("No trailer found on YouTube");
      }
    } catch (error) {
      console.error("Error searching YouTube:", error);
      alert("Error searching for trailer");
    } finally {
      setIsLoadingTrailer(false);
    }
  };

  const handleSearch = () => {
    if (searchTerm.trim() !== "") {
      searchMovies(searchTerm);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleMovieClick = (movie) => {
    setSelectedMovie(movie);
    setShowModal(true);
    setYoutubeVideoId(null);
    setIsMuted(false);
    setIsFullscreen(false);
  };

  const closeModal = () => {
    setSelectedMovie(null);
    setShowModal(false);
    setYoutubeVideoId(null);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (iframeRef.current.requestFullscreen) {
        iframeRef.current.requestFullscreen();
      } else if (iframeRef.current.webkitRequestFullscreen) {
        iframeRef.current.webkitRequestFullscreen();
      } else if (iframeRef.current.msRequestFullscreen) {
        iframeRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className="app">
      <h1>ENTERTAINMENT ZONE!</h1>

      <div className="search">
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search for movies"
          onKeyPress={handleKeyPress}
        />
        <img src={SearchIcon} alt="search" onClick={handleSearch} />
      </div>

      {movies?.length > 0 ? (
        <div className="container">
          {movies.map((movie) => (
            <MovieCard key={movie.imdbID} movie={movie} onClick={() => handleMovieClick(movie)} />
          ))}
        </div>
      ) : (
        <div className="empty">
          <h2>No movies found</h2>
        </div>
      )}

      {showModal && selectedMovie && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={closeModal}>&times;</span>
            <h2>{selectedMovie.Title} ({selectedMovie.Year})</h2>
            <img 
              src={selectedMovie.Poster !== "N/A" ? selectedMovie.Poster : "https://via.placeholder.com/400"} 
              alt={selectedMovie.Title} 
            />
            <p><strong>Genre:</strong> {selectedMovie.Genre}</p>
            <p><strong>IMDb Rating:</strong> {selectedMovie.imdbRating}</p>
            <p><strong>Plot:</strong> {selectedMovie.Plot}</p>
            <p><strong>Director:</strong> {selectedMovie.Director}</p>
            <p><strong>Actors:</strong> {selectedMovie.Actors}</p>
            <p><strong>Runtime:</strong> {selectedMovie.Runtime}</p>
            
            <div className="streaming-section">
              <button 
                onClick={() => searchYoutubeTrailer(selectedMovie.Title, selectedMovie.Year)}
                disabled={isLoadingTrailer}
                className="trailer-button"
              >
                {isLoadingTrailer ? "Loading..." : "Watch Trailer"}
              </button>
              
              {youtubeVideoId && (
                <div className={`youtube-container ${isFullscreen ? 'fullscreen' : ''}`}>
                  <div className="video-controls">
                    <button onClick={toggleMute} className="control-button">
                      {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                    </button>
                    <button onClick={toggleFullscreen} className="control-button">
                      <FaExpand />
                    </button>
                  </div>
                  <iframe
                    ref={iframeRef}
                    width="560"
                    height="315"
                    src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=${isMuted ? 1 : 0}`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;